const INTERACTIVE_TOGGLE_SELECTOR = '[data-mf-toggle-target]';
const INTERACTIVE_ACCORDION_TRIGGER_SELECTOR = '[data-mf-accordion-trigger]';
const INTERACTIVE_SWITCHER_TRIGGER_SELECTOR = '[data-mf-switcher-trigger]';

export const INTERACTIVE_CONTROL_SELECTOR = [
    INTERACTIVE_TOGGLE_SELECTOR,
    INTERACTIVE_ACCORDION_TRIGGER_SELECTOR,
    INTERACTIVE_SWITCHER_TRIGGER_SELECTOR,
].join(', ');

const splitClassTokens = (value = '') => String(value)
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean);

const resolveTarget = (root, trigger, attributeName = 'data-mf-target') => {
    const selector = trigger.getAttribute(attributeName);

    if (!selector) {
        return null;
    }

    return root.querySelector(selector) ?? root.ownerDocument?.querySelector?.(selector) ?? null;
};

const setHiddenState = (element, shouldHide) => {
    if (!element) {
        return;
    }

    element.classList.toggle('hidden', shouldHide);

    if (shouldHide) {
        element.setAttribute('hidden', '');
    } else {
        element.removeAttribute('hidden');
    }
};

const isHidden = (element) => {
    return element?.hasAttribute('hidden') || element?.classList?.contains('hidden');
};

const syncTriggerState = (trigger, isActive, ariaAttribute = 'aria-expanded') => {
    trigger.setAttribute(ariaAttribute, isActive ? 'true' : 'false');

    const activeClasses = splitClassTokens(trigger.getAttribute('data-mf-active-class'));
    const inactiveClasses = splitClassTokens(trigger.getAttribute('data-mf-inactive-class'));

    activeClasses.forEach((className) => {
        trigger.classList.toggle(className, isActive);
    });

    inactiveClasses.forEach((className) => {
        trigger.classList.toggle(className, !isActive);
    });
};

const syncToggleTrigger = (root, trigger) => {
    const target = resolveTarget(root, trigger, 'data-mf-toggle-target');
    syncTriggerState(trigger, target ? !isHidden(target) : false, 'aria-pressed');
};

const syncAccordionState = (root) => {
    root.querySelectorAll('[data-mf-accordion]').forEach((accordion) => {
        accordion.querySelectorAll(INTERACTIVE_ACCORDION_TRIGGER_SELECTOR).forEach((trigger) => {
            const target = resolveTarget(accordion, trigger);
            syncTriggerState(trigger, target ? !isHidden(target) : false);
        });
    });
};

const activateSwitcherTrigger = (switcher, trigger) => {
    switcher.querySelectorAll(INTERACTIVE_SWITCHER_TRIGGER_SELECTOR).forEach((candidate) => {
        const target = resolveTarget(switcher, candidate);
        const isActive = candidate === trigger;
        syncTriggerState(candidate, isActive, 'aria-pressed');
        setHiddenState(target, !isActive);
    });
};

const initializeToggles = (root) => {
    root.querySelectorAll(INTERACTIVE_TOGGLE_SELECTOR).forEach((trigger) => {
        syncToggleTrigger(root, trigger);
    });
};

const initializeAccordions = (root) => {
    root.querySelectorAll('[data-mf-accordion]').forEach((accordion) => {
        accordion.querySelectorAll(INTERACTIVE_ACCORDION_TRIGGER_SELECTOR).forEach((trigger) => {
            const target = resolveTarget(accordion, trigger);
            syncTriggerState(trigger, target ? !isHidden(target) : false);
        });
    });
};

const initializeSwitchers = (root) => {
    root.querySelectorAll('[data-mf-switcher]').forEach((switcher) => {
        const triggers = Array.from(switcher.querySelectorAll(INTERACTIVE_SWITCHER_TRIGGER_SELECTOR));

        if (triggers.length === 0) {
            return;
        }

        const activeTrigger = triggers.find((trigger) => trigger.getAttribute('data-mf-active') === 'true')
            ?? triggers.find((trigger) => {
                const target = resolveTarget(switcher, trigger);
                return target && !isHidden(target);
            })
            ?? triggers[0];

        activateSwitcherTrigger(switcher, activeTrigger);
    });
};

export const mountInteractiveRuntime = (root) => {
    if (!root) {
        return () => {};
    }

    initializeToggles(root);
    initializeAccordions(root);
    initializeSwitchers(root);

    const handleClick = (event) => {
        const trigger = event.target.closest(INTERACTIVE_CONTROL_SELECTOR);

        if (!trigger || !root.contains(trigger)) {
            return;
        }

        if (trigger.matches(INTERACTIVE_TOGGLE_SELECTOR)) {
            const target = resolveTarget(root, trigger, 'data-mf-toggle-target');

            if (!target) {
                return;
            }

            event.preventDefault();
            const nextActiveState = isHidden(target);
            setHiddenState(target, !nextActiveState);
            syncTriggerState(trigger, nextActiveState, 'aria-pressed');
            return;
        }

        if (trigger.matches(INTERACTIVE_ACCORDION_TRIGGER_SELECTOR)) {
            const accordion = trigger.closest('[data-mf-accordion]');

            if (!accordion) {
                return;
            }

            const target = resolveTarget(accordion, trigger);

            if (!target) {
                return;
            }

            event.preventDefault();
            const shouldOpenTarget = isHidden(target);

            accordion.querySelectorAll(INTERACTIVE_ACCORDION_TRIGGER_SELECTOR).forEach((candidate) => {
                const candidateTarget = resolveTarget(accordion, candidate);
                const isActive = shouldOpenTarget && candidate === trigger;
                setHiddenState(candidateTarget, !isActive);
                syncTriggerState(candidate, isActive);
            });

            return;
        }

        if (trigger.matches(INTERACTIVE_SWITCHER_TRIGGER_SELECTOR)) {
            const switcher = trigger.closest('[data-mf-switcher]');

            if (!switcher) {
                return;
            }

            event.preventDefault();
            activateSwitcherTrigger(switcher, trigger);
        }
    };

    root.addEventListener('click', handleClick);

    return () => {
        root.removeEventListener('click', handleClick);
    };
};
